import type { ComponentPropsWithoutRef } from 'react'

import { styled } from 'styled-components'

import { ButtonAsSpan } from '@b-stock/fe-core/bstock-react'

import LegacyBpLink from '@/buyer-portal/components/LegacyBpLink'

const DisabledWrapper = styled.span`
  opacity: 0.5;
  pointer-events: none;
  cursor: not-allowed;
`

type ButtonLinkProps = {
  href: string
  disabled?: boolean
} & ComponentPropsWithoutRef<typeof ButtonAsSpan> &
  ComponentPropsWithoutRef<typeof LegacyBpLink>

/**
 * WARNING: Do not remove this component because imported from bstock-next it works incorrectly.
 */

/**
 * Wraps bstock-react's ButtonAsSpan component with a LegacyBpLink component, to allow for accessible client-side routing.
 * Uses ButtonAsSpan to avoid button-inside-link accessibility violations.
 *
 * @example
 * ```tsx
 * import { ButtonLink } from '@/buyer-portal/components/ButtonLink'
 *
 * const DisputeIdLink = ({ disputeId }: { disputeId: string }) => (
 *   <ButtonLink
 *     appearance="plainTertiary"
 *     href={`/disputes/${encodeURIComponent(disputeId)}/details`}
 *   >
 *     {disputeId}
 *   </ButtonLink>
 * )
 * ```
 *
 * @category Common
 */
export const ButtonLink = ({
  href,
  appearance,
  size,
  active,
  children,
  disabled,
  ...linkProps
}: ButtonLinkProps) => {
  // When disabled, render just the ButtonAsSpan without the link wrapper
  if (disabled) {
    return (
      <DisabledWrapper>
        <ButtonAsSpan appearance={appearance} size={size} active={false}>
          {children}
        </ButtonAsSpan>
      </DisabledWrapper>
    )
  }

  return (
    <LegacyBpLink href={href} {...linkProps}>
      <ButtonAsSpan appearance={appearance} size={size} active={active}>
        {children}
      </ButtonAsSpan>
    </LegacyBpLink>
  )
}
